import React, { useCallback, useMemo, useState, SetStateAction, Dispatch } from 'react';
import { IStyle } from 'Styles';
import '../sections/style.scss';
import { handleTableFilter, BaseGroup } from '../sections/baseGroup';
import { GroupDTO, TreeNodeAccessRightDTO, UserDTO } from 'business/models/Models';
import { NodeAccessRightsType } from './index';
import RMButton from 'components/customComponents/Controls/RMButton';
import { AccessRightType } from 'constants/';

interface Props {
  state: NodeAccessRightsType;
  setState: Dispatch<SetStateAction<NodeAccessRightsType>>;
  nodeTitle: string;
}

interface ITableColumn {
  accessor?: any;
  Filter?: any;
  Header?: any;
}

const tableColumns: ITableColumn[] = [
  {
    accessor: 'type',
    Filter: handleTableFilter('Type'),
  },
  {
    accessor: 'name',
    Filter: handleTableFilter('Name'),
  },
  {
    accessor: 'membersCount',
    Filter: handleTableFilter('No of Members'),
  },
];

const UserGroupWithoutAccess = (props: Props) => {
  const { state, setState } = props;
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const consolidatedDataSet = useMemo(
    () => [...state.data?.groupsWithoutAccess, ...state.data?.usersWithoutAccess],
    [state, selectedRows],
  );

  const accessRightsData = useMemo(
    () =>
      consolidatedDataSet?.map((accessRight: GroupDTO & UserDTO) => ({
        type: accessRight.email ? 'User' : 'Group',
        name: accessRight.name,
        membersCount: accessRight.users ? accessRight.users.length : '-',
      })) || [],
    [state, selectedRows, consolidatedDataSet],
  );

  const handleAllowAccess = useCallback(() => {
    const _allowAccess: TreeNodeAccessRightDTO[] = [];
    const _usersWithoutAccess: UserDTO[] = [];
    const _groupsWithoutAccess: GroupDTO[] = [];
    consolidatedDataSet.forEach((row: GroupDTO & UserDTO, _ix: number) => {
      const _includes = selectedRows.includes(_ix);

      if (_includes) {
        _allowAccess.push({
          accessRight: [{ id: AccessRightType.READ, init: null, toJSON: null }],
          user: row.email ? row : null,
          group: !row.email ? row : null,
          init: null,
          toJSON: null,
        });
      } else {
        row.email ? _usersWithoutAccess.push(row) : _groupsWithoutAccess.push(row);
      }
    });
    setSelectedRows([]);

    setState((prv) => ({
      ...prv,
      data: {
        ...prv.data,
        withAccessRights: [...prv.data.withAccessRights, ..._allowAccess],
        usersWithoutAccess: _usersWithoutAccess,
        groupsWithoutAccess: _groupsWithoutAccess,
      },
    }));
  }, [selectedRows, consolidatedDataSet]);

  const handleSelectionChange = useCallback((rowIndices) => {
    setSelectedRows([...rowIndices]);
  }, []);

  const renderAllowAccessButton = useMemo(() => {
    const isDisabled = !selectedRows.length;
    const disabledStyle: React.CSSProperties = isDisabled
      ? styles.allowAccessBtnDisabled
      : {};
    return (
      <RMButton
        disabled={isDisabled}
        onClick={handleAllowAccess}
        customStyle={{
          ...styles.allowAccessBtn,
          ...disabledStyle,
        }}
        label="Allow Access"
      />
    );
  }, [selectedRows]);

  return (
    <div id="usergroupwithoutaccess-wrapper" style={styles.root}>
      <div id="usergroupwithoutaccess-listnoaccess-title" className="font-bold">
        List of Groups/Users with no direct access
      </div>
      <BaseGroup
        actionContainerProps={{
          customButton: renderAllowAccessButton,
        }}
        headerTitle={'Groups/Users'}
        tableProps={{
          columns: tableColumns,
          data: accessRightsData,
          onCheckboxSelectionChange: handleSelectionChange,
          selectedIds: selectedRows,
        }}
        tableContainerStyle={styles.tableContainer}
      />
    </div>
  );
};

const styles: IStyle = {
  root: {
    flexDirection: 'row',
  },
  headerContainer: {
    padding: 20,
    paddingTop: 8,
  },
  headerTitle: {
    fontWeight: 'bold',
  },
  allowAccessBtn: {
    width: '100%',
    backgroundColor: '#3F9243',
  },
  allowAccessBtnDisabled: {
    backgroundColor: '#E0E0E0',
    color: '#CACACA',
  },
  tableContainer: {
    height: '350px',
  },
};

export default UserGroupWithoutAccess;
